import { useState, useCallback, useMemo } from "react";

import { startOfWeek, setHours, setMinutes, setSeconds } from "date-fns";
import { UUID } from "@screencloud/uuid";

const now = new Date();

const DEFAULT_DATE_RANGE = {
  start: startOfWeek(now, { weekStartsOn: 1 }),
  end: setHours(setMinutes(setSeconds(now, 59), 59), 23),
};

const INITIAL_FILTERS: FilterState = {
  spaceId: null,
  userId: null,
  eventType: null,
  entityId: null,
  entityTypes: null,
  dateRange: {
    start: DEFAULT_DATE_RANGE.start,
    end: DEFAULT_DATE_RANGE.end,
  },
  includeSystemLogs: true,
};

export type DateRange = {
  start: Date;
  end: Date;
};

export interface FilterState {
  spaceId?: UUID | null;
  userId?: UUID | null;
  eventType?: "I" | "U" | "D" | null;
  entityId?: UUID | null;
  entityTypes?: string[] | null;
  dateRange?: DateRange;
  includeSystemLogs?: boolean;
}

const getEntityTypes = (entityType: string) => {
  switch (entityType) {
    case "screen":
      return ["screen"];
    case "screen_group":
      return ["screen_group"];
    case "cast":
      return ["cast"];
    case "file":
      return ["file", "folder"];
    case "channel":
      return ["channel"];
    case "layout":
      return ["layout"];
    case "playlist":
      return ["playlist"];
    case "link":
      return ["link"];
    case "site":
      return ["site"];
    case "app":
      return ["app", "app_install", "app_instance"];
    case "org":
      return [
        "org",
        "org_connection",
        "permission_set",
        "feature_flag_org",
        "space",
        "system_access",
        "theme",
        "token",
        "user",
        "user_group",
        "group",
        "group_space",
        "credential",
      ];
    default:
      return null;
  }
};

export const useAuditLogFilter = () => {
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [isPersonDropdownOpen, setIsPersonDropdownOpen] = useState(false);

  const handleFilterChange = useCallback(
    (filterType: keyof FilterState) =>
      (
        e: React.SyntheticEvent,
        data: { value: string | DateRange | boolean | null },
      ) => {
        if (filterType === "dateRange") {
          setFilters((prev) => {
            return {
              ...prev,
              dateRange: data.value as DateRange,
            };
          });
        } else if (filterType === "entityTypes") {
          setFilters((prev) => ({
            ...prev,
            entityTypes: data.value
              ? getEntityTypes(data.value as string)
              : null,
          }));
        } else {
          setFilters((prev) => ({
            ...prev,
            [filterType]: data.value,
          }));
        }
      },
    [],
  );

  const handlePersonDropdownOpen = useCallback(() => {
    setIsPersonDropdownOpen(true);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters((prev) => ({
      ...INITIAL_FILTERS,
      dateRange: prev.dateRange, // keep the current dateRange
    }));
  }, []);

  // Check if any filter is active (as a variable)
  const isAnyFilterActive = useMemo(() => {
    if (filters.spaceId !== INITIAL_FILTERS.spaceId) return true;
    if (filters.userId !== INITIAL_FILTERS.userId) return true;
    if (filters.eventType !== INITIAL_FILTERS.eventType) return true;
    if (filters.entityId !== INITIAL_FILTERS.entityId) return true;
    if (
      (filters.entityTypes &&
        JSON.stringify(filters.entityTypes) !==
          JSON.stringify(INITIAL_FILTERS.entityTypes)) ||
      (!filters.entityTypes && INITIAL_FILTERS.entityTypes)
    ) {
      return true;
    }
    return false;
  }, [filters]);

  return {
    filters,
    isPersonDropdownOpen,
    handleFilterChange,
    handlePersonDropdownOpen,
    resetFilters,
    DEFAULT_DATE_RANGE,
    isAnyFilterActive,
  };
};
